import { Field, MultiSelectSearch } from "@/components/ui";
import { Flag } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import { useAddAlerts, useRemoveAlerts } from "../network/useAlerts";
interface AlertsEditorProps {
  patientId: string;
  existingFlagCodes: string[] | undefined;
  closeEditor: () => void;
}

export const AlertsEditor = ({
  patientId,
  existingFlagCodes,
  closeEditor,
}: AlertsEditorProps) => {
  const { mutate: addAlerts, isPending: isSavingAddingAlerts } = useAddAlerts({
    patientId,
    onSuccess: () => {},
  });
  const { mutate: removeAlerts, isPending: isSavingRemovingAlerts } =
    useRemoveAlerts({
      patientId,
      onSuccess: () => {},
    });

  const alertsFormSchema = z.object({
    selectedFlagCodes: z.array(z.string()),
  });

  const form = useForm<z.infer<typeof alertsFormSchema>>({
    resolver: zodResolver(alertsFormSchema),
    defaultValues: {
      selectedFlagCodes: existingFlagCodes || [],
    },
  });

  const watchSelectedFlagCodes = form.watch("selectedFlagCodes");

  const options = useMemo(() => {
    return Object.values(Flag.FlagCode.values)
      .map((code) => ({
        label: Flag.FlagCodeDisplay[code as keyof typeof Flag.FlagCodeDisplay],
        value: code,
      }))
      .filter((option) => {
        return !watchSelectedFlagCodes?.includes(option.value);
      });
  }, [watchSelectedFlagCodes]);

  const handleSubmit = (values: z.infer<typeof alertsFormSchema>) => {
    const toAdd = values.selectedFlagCodes.filter(
      (code) => !existingFlagCodes?.includes(code),
    );
    const toRemove = existingFlagCodes?.filter(
      (code) => !values.selectedFlagCodes.includes(code),
    );
    if (toAdd.length) {
      addAlerts({ flags: toAdd });
    }
    if (toRemove?.length) {
      removeAlerts({ flags: toRemove });
    }
    closeEditor();
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col py-3 gap-2"
    >
      <Controller
        control={form.control}
        name="selectedFlagCodes"
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid}>
            <MultiSelectSearch
              autoFocusInput
              openOnFocus
              options={options}
              selectedItems={field.value.map((code) => ({
                label:
                  Flag.FlagCodeDisplay[
                    code as keyof typeof Flag.FlagCodeDisplay
                  ],
                value: code,
              }))}
              onInputChange={() => {}}
              onItemAdded={(item) => {
                form.setValue("selectedFlagCodes", [
                  ...field.value,
                  item.value.toString(),
                ]);
              }}
              onItemRemoved={(item) => {
                form.setValue(
                  "selectedFlagCodes",
                  [...field.value].filter((code) => code !== item.value),
                );
              }}
            />
          </Field>
        )}
      />
      <SubmitCancelButtons
        isSaving={isSavingAddingAlerts || isSavingRemovingAlerts}
        onCancel={closeEditor}
      />
    </form>
  );
};
